// plugins/firebaseAuth.ts
import { initializeApp, getApps, deleteApp, type FirebaseOptions } from 'firebase/app'
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth'
import { initUser } from '~/composables/useFirebase'
import { connectFirebaseAuthEmulatorIfConfigured } from '~/utils/firebaseAuthEmulator'
import { useRuntimeConfig, defineNuxtPlugin } from '#app'

/** Reject empty values and unexpanded CI placeholders like `$FIREBASE_PROJECT_ID`. */
const resolveConfigString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed || /^\$[A-Z0-9_]+$/.test(trimmed)) return undefined
  return trimmed
}

/**
 * Prefer explicit projectId; fall back to the authDomain subdomain
 * (`my-app.firebaseapp.com` → `my-app`) so Auth v2 APIs never see an empty project.
 */
const resolveFirebaseProjectId = (
  projectId: unknown,
  authDomain: unknown,
): string | undefined => {
  const explicit = resolveConfigString(projectId)
  if (explicit) return explicit

  const host = resolveConfigString(authDomain)?.split(':')[0]
  if (!host) return undefined
  if (host.endsWith('.firebaseapp.com')) {
    return host.slice(0, -'.firebaseapp.com'.length) || undefined
  }
  if (host.endsWith('.web.app')) {
    return host.slice(0, -'.web.app'.length) || undefined
  }
  return undefined
}

export default defineNuxtPlugin({
  name: 'firebase-auth',
  enforce: 'pre',
  async setup(nuxtApp) {
    const config = useRuntimeConfig()

    const apiKey = resolveConfigString(config.public.firebaseApiKey)
    const authDomain = resolveConfigString(config.public.firebaseAuthDomain)
    const projectId = resolveFirebaseProjectId(
      config.public.firebaseProjectId,
      authDomain,
    )

    if (!apiKey || !authDomain || !projectId) {
      const missing = [
        !apiKey && 'firebaseApiKey',
        !authDomain && 'firebaseAuthDomain',
        !projectId && 'firebaseProjectId',
      ].filter(Boolean)
      const message = `Firebase Auth is misconfigured (missing: ${missing.join(', ')}). Check FIREBASE_* build env vars.`
      // Do not initializeApp without projectId — that yields auth/missing-project-id later.
      $reportError(message)
      return
    }

    const firebaseConfig: FirebaseOptions = {
      apiKey,
      authDomain,
      projectId,
      storageBucket: resolveConfigString(config.public.firebaseStorageBucket),
      messagingSenderId: resolveConfigString(config.public.firebaseMessagingSenderId),
      appId: resolveConfigString(config.public.firebaseAppId),
      measurementId: resolveConfigString(config.public.firebaseMeasurementId),
    }

    // Reuse an existing default app only when it already has a projectId.
    // Otherwise delete and re-init — leaving a project-less app registered makes
    // later getAuth() calls keep failing with auth/missing-project-id.
    const existingApp = getApps()[0]
    if (existingApp && !resolveConfigString(existingApp.options.projectId)) {
      $reportError('Firebase default app was initialized without projectId; reinitializing')
      await deleteApp(existingApp)
    }
    const app = getApps()[0] ?? initializeApp(firebaseConfig)

    const auth = getAuth(app)

    // Set persistence (browser-only API)
    if (import.meta.client) {
      connectFirebaseAuthEmulatorIfConfigured(
        auth,
        config.public.firebaseAuthEmulatorUrl,
      )
      await setPersistence(auth, browserLocalPersistence).catch((error) => {
        $reportError('Failed to set persistence:', error)
      })

      // Initialize user state after persistence is ready
      initUser()
    }

    // Provide auth and app objects globally
    nuxtApp.provide('auth', auth)
    nuxtApp.provide('firebaseApp', app)
  },
})
